// @flow
import React, { PureComponent, Fragment } from 'react';
import type { AnnuaireType, MemberType } from 'types/PageDetail';
import Highlighter from "react-highlight-words";
import DropdownRadioInput from "components/fragments/input/DropdownRadioInput";
import { dynamicClassName } from 'utils/dynamicClassName';

type Props = {
  annuaires: Array<AnnuaireType>,
  title: string,
};

type ComponentState = {
  search: string,
  annuaireSelected: string,
  department: string,
  isOpen: boolean,
  indexOpen: number,
  annuaireSelectorDisabled: boolean
}

class AnnuairesPageDetail extends PureComponent <Props, ComponentState> {
  state: ComponentState = {
    search: '',
    department: '',
    annuaireSelected: '',
    annuaireSelectorDisabled: false,
    isOpen: false,
    indexOpen: 0
  };

  componentDidMount = () => {
    if (!this.state.annuaireSelected && this.props.annuaires.length > 0) {
      this.setState({ annuaireSelected : this.props.annuaires[0].title });
    }
  }

  renderRow = (personnes: Array<MemberType>): any => {
    const searchWords = this.state.search.split(' ');
    if (personnes && personnes.length > 0) {
      return personnes.map<any>((member: MemberType, index): any => {
        return (
          <li key={index} className="member member--row">
            <div>
              <p className="member__title ft-h4">
                <Highlighter
                  searchWords={searchWords}
                  autoEscape={true}
                  textToHighlight={member.title}
                />
              </p>
              {member.roles &&
                <p className="member__role ft-h6 ft-up">
                  {member.roles.map<any>((role, index) => {
                    return(
                      <Fragment key={index}>
                        <Highlighter
                          searchWords={searchWords}
                          autoEscape={true}
                          textToHighlight={role}
                        />
                        <br/>
                      </Fragment>
                    );
                  })}
                </p>
              }
            </div>
            <p className="member__info">
              {member.email &&
                <a
                  href={`mailto:${member.email}`}
                  target="_blank"
                  rel="noopener noreferrer">
                  <i className="icon icon-mail is-inline"></i>
                    <Highlighter
                      searchWords={searchWords}
                      autoEscape={true}
                      textToHighlight={member.email}
                    />
                </a>
              }
              {member.telephone &&
                <a href={`tel:${member.telephone}`} target="_blank" rel="noopener noreferrer">
                  <i className="icon icon-phone is-inline"></i>
                  <span>
                    <Highlighter
                      searchWords={searchWords}
                      autoEscape={true}
                      textToHighlight={member.telephone}
                    />
                  </span>
                </a>
              }
            </p>
          </li>
        );
      });
    }
    return '';
  }

  renderGroup = () => {
    const { annuaires } = this.props;
    let membersGroupsFiltered = annuaires;

    if (!annuaires || !membersGroupsFiltered) {
      return null;
    }

    if (!this.state.annuaireSelectorDisabled && this.state.annuaireSelected) {
      membersGroupsFiltered = membersGroupsFiltered.filter(annuaire => { 
        return annuaire.title === this.state.annuaireSelected; }
      );
    }

    if (this.state.search !== '') {
      membersGroupsFiltered = membersGroupsFiltered.map<any>(annuaire => {
        return {
          ...annuaire,
          personnes: annuaire.personnes.map<any>((group) => {
            return {
              name : group.name, 
              child: group.child.filter(member =>
                (member.title && member.title.toLowerCase().includes(this.state.search.toLowerCase()))
                || (member.role && member.role.toLowerCase().includes(this.state.search.toLowerCase()))
                || (member.email && member.email.toLowerCase().includes(this.state.search.toLowerCase()))
                || (member.telephone && member.telephone.toLowerCase().includes(this.state.search.toLowerCase()))
              )
            };
          }).filter( (group) => {
            return group.child.length > 0;
          })
        };
      });
    }

    membersGroupsFiltered = membersGroupsFiltered.filter(annuaire => {
      return annuaire.personnes.length > 0;
    });
    if (membersGroupsFiltered.length > 0) {
      return membersGroupsFiltered.map<any>((annuaire: {title: string, personnes: Array<{
        name: string,
        child: Array<MemberType>
      }>}, index) => {
        if (annuaire.personnes.length > 0) {
          return (
            <Fragment key={'lettre_'+index}>
              <h3 className={'mb-3'}>{annuaire.title}</h3>
              <div className="row directory">
                {annuaire.personnes.map<any>((members, index) => {
                  if (members.child.length > 0) {
                    return (
                      <Fragment key={index}>
                        <div className="col col-lg-1on9">
                          <span className="directory__ref ft-700">{members.name}</span>
                        </div>
                        <div className="col col-lg-7on9">
                          <div className="directory__items">
                            <ul className="directory__members box box--smoke">
                              {this.renderRow(members.child.sort((a, b) => {
                                if (a.title.toLowerCase() < b.title.toLowerCase()) return -1;
                                if (a.title.toLowerCase() > b.title.toLowerCase()) return 1;
                                return 0;
                              }))}
                            </ul>
                          </div>
                        </div>
                      </Fragment>
                    );
                  }
                  return '';
                })}
              </div>
            </Fragment>
          );
        } else if (this.state.search !== '') {
          return (
            <div className="placeholder">
              <p className="ft-h5">
                <i className="icon icon-articles-empty"></i>
                <br />Aucune personne pour cette recherche
              </p>
            </div>
          );
        }
        return null;
      });
    } else if (this.state.search !== '') {
      return (
        <div className="placeholder">
          <p className="ft-h5">
            <i className="icon icon-articles-empty"></i>
            <br />Aucune personne pour cette recherche
          </p>
        </div>
      );
    }
    return null;
  }

  handleClickDepartment = (index: number) => {
    return () => {
      if (this.state.isOpen && index !== this.state.indexOpen) {
        this.setState({
          indexOpen: index
        });
      } else {
        this.setState(prevState => ({
          isOpen: !prevState.isOpen,
          indexOpen: index
        }));
      }
    };
  }

  handleChange = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      this.setState({
        search: e.target.value,
        annuaireSelectorDisabled: e.target.value !== ''
      });
    }
  }

  handleDropdownAnnuaireChange = (value: string) => {
    this.setState({
      annuaireSelected: value,
    });
  }

  render() {
    const { annuaires } = this.props;
    const { isOpen } = this.state;

    const rowClassName = dynamicClassName("row directory");
    isOpen && rowClassName.add("has-accordion-open");
    if (annuaires) {
      const annuairesValue = annuaires.map<any>((annuaire, index) => {
        return { value: annuaire.title, label: annuaire.title };
      });
      return (
        <Fragment>
          <div className="page__head mb-3 mt-4">
            <DropdownRadioInput 
              options={annuairesValue}
              inputChanged={this.handleDropdownAnnuaireChange}
              isActive={!this.state.annuaireSelectorDisabled} />
            <div className="input input--search" data-overlay="false">
              <input 
                type="search" 
                id="ioudf892" 
                placeholder="Saisir le nom ou une fonction" 
                onChange={this.handleChange} />
              <label htmlFor="ioudf892">Saisir le nom ou un département</label>
            </div>
          </div>
          {this.renderGroup()}
        </Fragment>
      );
    }
    return null;
  }
}

export default AnnuairesPageDetail;
